import React from "react";
import { LocateFixed, MapPin, Search, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ICON_STROKE } from "../ui/icon";
import { formatLastUpdated } from "./nearby-utils";

const NearbySearchPanel = ({
  mode,
  onModeChange,
  pincode,
  onPincodeChange,
  onPincodeSubmit,
  onLocateMe,
  locationGranted,
  searchRadius,
  hasGeolocation,
  lastUpdated,
}) => {
  const radiusKm = searchRadius ? (searchRadius / 1000).toFixed(0) : "10";
  const isLocationTab = mode === "gps";
  const showLocationActive = isLocationTab && locationGranted;
  const updatedLabel = lastUpdated ? formatLastUpdated(lastUpdated) : "Updated a few seconds ago";

  return (
    <div className="nearby-search-card">
      {hasGeolocation ? (
        <div className="nearby-search-tabs" role="tablist" aria-label="Search method">
          <button
            type="button"
            role="tab"
            id="nearby-tab-location"
            aria-selected={isLocationTab}
            aria-controls="nearby-panel-location"
            className={`nearby-search-tabs__tab${isLocationTab ? " nearby-search-tabs__tab--active" : ""}`}
            onClick={() => onModeChange("gps")}
          >
            <LocateFixed size={15} strokeWidth={ICON_STROKE} />
            <span>Current Location</span>
          </button>
          <button
            type="button"
            role="tab"
            id="nearby-tab-pincode"
            aria-selected={!isLocationTab}
            aria-controls="nearby-panel-pincode"
            className={`nearby-search-tabs__tab${!isLocationTab ? " nearby-search-tabs__tab--active" : ""}`}
            onClick={() => onModeChange("pincode")}
          >
            <MapPin size={15} strokeWidth={ICON_STROKE} />
            <span>PIN Code</span>
          </button>
        </div>
      ) : (
        <div className="nearby-search-tabs nearby-search-tabs--single">
          <span className="nearby-search-tabs__single-label">
            <MapPin size={15} strokeWidth={ICON_STROKE} />
            Search by PIN Code
          </span>
        </div>
      )}

      {isLocationTab && hasGeolocation ? (
        <div
          id="nearby-panel-location"
          role="tabpanel"
          aria-labelledby="nearby-tab-location"
          className="nearby-search-panel"
        >
          {showLocationActive ? (
            <div className="nearby-search-card__location">
              <div className="nearby-search-card__location-main">
                <div className="nearby-search-card__gps-icon" aria-hidden>
                  <LocateFixed size={18} strokeWidth={ICON_STROKE} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="nearby-search-card__value">Using Current Location</p>
                  <div className="nearby-search-card__meta-row">
                    <span className="nearby-search-card__radius-chip">Within {radiusKm} km</span>
                    <span className="nearby-search-card__gps-chip">
                      <CheckCircle2 size={12} strokeWidth={ICON_STROKE} />
                      GPS Active
                    </span>
                  </div>
                  <p className="nearby-search-card__updated">{updatedLabel}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="nearby-search-location-prompt">
              <div className="nearby-search-location-prompt__icon" aria-hidden>
                <LocateFixed size={22} strokeWidth={ICON_STROKE} />
              </div>
              <p className="nearby-search-location-prompt__title">Find services near you</p>
              <p className="nearby-search-location-prompt__text">
                Allow location access to see hospitals, police, and emergency services closest to you.
              </p>
              <Button type="button" className="nearby-search-location-prompt__btn" onClick={onLocateMe}>
                <LocateFixed size={16} strokeWidth={ICON_STROKE} />
                Use My Location
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          id="nearby-panel-pincode"
          role="tabpanel"
          aria-labelledby={hasGeolocation ? "nearby-tab-pincode" : undefined}
          className="nearby-search-panel"
        >
          <p className="nearby-search-card__pin-hint">
            Enter a 6-digit Indian PIN to find emergency services in that area
          </p>
          <form onSubmit={onPincodeSubmit} className="nearby-search-card__pin-form">
            <div className="nearby-search-card__pin-field">
              <Search size={16} strokeWidth={ICON_STROKE} className="nearby-search-card__pin-field-icon" />
              <Input
                id="nearby-pincode"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pincode}
                onChange={onPincodeChange}
                placeholder="e.g. 110001"
                className="nearby-search-card__pin-input"
                aria-label="PIN code"
              />
            </div>
            <Button type="submit" disabled={pincode.length !== 6} className="nearby-search-card__pin-btn">
              Search
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default NearbySearchPanel;
